// ONE POS asosiy komponentlari. Ko'rinish tokens.css'dagi klasslardan keladi.
import { useId } from 'react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Tugma
// ---------------------------------------------------------------------------
export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'mint' | 'ghost';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
  /** Faqat belgi (ikonka) bo'lsa — aria-label majburiy */
  iconOnly?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  iconOnly = false,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        'op-btn',
        `op-btn--${variant}`,
        size === 'sm' && 'op-btn--sm',
        iconOnly && 'op-btn--icon',
        className,
      )}
      {...rest}
    />
  );
}

// ---------------------------------------------------------------------------
// Belgi (badge)
// ---------------------------------------------------------------------------
export type Tone = 'success' | 'info' | 'warning' | 'danger' | 'neutral';

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={cx('op-badge', `op-badge--${tone}`)}>{children}</span>;
}

// ---------------------------------------------------------------------------
// Karta
// ---------------------------------------------------------------------------
export function Card({
  title,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx('op-card', className)}>
      {(title || actions) && (
        <div className="op-card__header">
          {title ? <h2 className="op-card__title">{title}</h2> : <span />}
          {actions}
        </div>
      )}
      {children !== undefined && <div className="op-card__body">{children}</div>}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Maydonlar
// ---------------------------------------------------------------------------
interface FieldShellProps {
  label: string;
  hint?: string;
  error?: string;
}

function FieldShell({
  id,
  label,
  hint,
  error,
  children,
}: FieldShellProps & { id: string; children: ReactNode }) {
  return (
    <div className="op-field">
      <label className="op-field__label" htmlFor={id}>
        {label}
      </label>
      {children}
      {error ? (
        <div className="op-field__error" id={`${id}-msg`} role="alert">
          {error}
        </div>
      ) : hint ? (
        <div className="op-field__hint" id={`${id}-msg`}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}

export function TextField({
  label,
  hint,
  error,
  id,
  className,
  ...rest
}: FieldShellProps & InputHTMLAttributes<HTMLInputElement>) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <FieldShell id={fid} label={label} hint={hint} error={error}>
      <input
        id={fid}
        className={cx('op-field__control', className)}
        aria-invalid={error ? true : undefined}
        aria-describedby={hint || error ? `${fid}-msg` : undefined}
        {...rest}
      />
    </FieldShell>
  );
}

export function TextAreaField({
  label,
  hint,
  error,
  id,
  className,
  ...rest
}: FieldShellProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <FieldShell id={fid} label={label} hint={hint} error={error}>
      <textarea
        id={fid}
        className={cx('op-field__control', className)}
        aria-invalid={error ? true : undefined}
        aria-describedby={hint || error ? `${fid}-msg` : undefined}
        {...rest}
      />
    </FieldShell>
  );
}

export interface SelectOption {
  value: string;
  label: string;
  /** Guruh nomi (optgroup): bir xil guruhdagilar ketma-ket bo'lishi kerak */
  group?: string;
}

/** Ketma-ket bir xil guruhdagilarni birlashtiradi */
function groupOptions(options: readonly SelectOption[]): [string, SelectOption[]][] {
  const out: [string, SelectOption[]][] = [];
  for (const o of options) {
    const g = o.group ?? '';
    const last = out[out.length - 1];
    if (last && last[0] === g) last[1].push(o);
    else out.push([g, [o]]);
  }
  return out;
}

export function SelectField({
  label,
  hint,
  error,
  id,
  options,
  className,
  ...rest
}: FieldShellProps & SelectHTMLAttributes<HTMLSelectElement> & { options: SelectOption[] }) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <FieldShell id={fid} label={label} hint={hint} error={error}>
      <select
        id={fid}
        className={cx('op-field__control', className)}
        aria-invalid={error ? true : undefined}
        aria-describedby={hint || error ? `${fid}-msg` : undefined}
        {...rest}
      >
        {options.some((o) => o.group)
          ? groupOptions(options).map(([g, list]) =>
              g ? (
                <optgroup key={g} label={g}>
                  {list.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </optgroup>
              ) : (
                list.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))
              ),
            )
          : options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
      </select>
    </FieldShell>
  );
}

// ---------------------------------------------------------------------------
// Bo'sh holat
// ---------------------------------------------------------------------------
export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="op-empty">
      <p className="op-empty__title">{title}</p>
      {children && <p style={{ margin: '0 0 16px' }}>{children}</p>}
      {action}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kalit (yoqish/o'chirish)
// ---------------------------------------------------------------------------
export function Switch({
  checked,
  onChange,
  label,
  disabled,
  description,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  description?: ReactNode;
}) {
  const id = useId();
  return (
    <div className="op-switch-row">
      <span className="op-switch-row__text">
        <label htmlFor={id} className="op-switch-row__label">
          {label}
        </label>
        {description && <span className="op-switch-row__desc">{description}</span>}
      </span>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        className={cx('op-switch', checked && 'op-switch--on')}
        onClick={() => onChange(!checked)}
      >
        <span className="op-switch__thumb" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ogohlantirish qatori
// ---------------------------------------------------------------------------
export function Alert({ tone = 'info', title, children }: { tone?: Tone; title?: ReactNode; children?: ReactNode }) {
  return (
    <div className={cx('op-alert', `op-alert--${tone}`)} role={tone === 'danger' ? 'alert' : 'status'}>
      {title && <strong className="op-alert__title">{title}</strong>}
      {children && <div>{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Yuklanmoqda
// ---------------------------------------------------------------------------
export function Spinner({ label = 'Yuklanmoqda…' }: { label?: string }) {
  return (
    <span className="op-spinner" role="status">
      <span className="op-spinner__dot" aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Oyna (dialog). Esc va fon bosilganda yopiladi (busy bo'lmasa).
// ---------------------------------------------------------------------------
export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  wide,
  tall,
  busy,
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children?: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** Balandligi doim bir xil (ro'yxat filtrlanganda oyna sakramasin) */
  tall?: boolean;
  busy?: boolean;
}) {
  const titleId = useId();
  if (!open) return null;
  return (
    <div
      className="op-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !busy) onClose();
      }}
    >
      <div className={cx('op-modal__box', wide && 'op-modal__box--wide', tall && 'op-modal__box--tall')} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="op-modal__head">
          <h2 id={titleId} className="op-modal__title">
            {title}
          </h2>
          <button type="button" className="op-btn op-btn--ghost op-btn--icon" aria-label="Yopish" onClick={onClose} disabled={busy}>
            <svg className="op-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <div className="op-modal__body">{children}</div>
        {footer && <div className="op-modal__foot">{footer}</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bo'limlar (tabs)
// ---------------------------------------------------------------------------
export function Tabs<K extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: readonly { key: K; label: ReactNode; count?: number; disabled?: boolean }[];
  value: K;
  onChange: (key: K) => void;
  label: string;
}) {
  return (
    <div className="op-tabs" role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={t.key === value}
          disabled={t.disabled}
          className={cx('op-tabs__tab', t.key === value && 'op-tabs__tab--on')}
          onClick={() => onChange(t.key)}
        >
          {t.label}
          {t.count !== undefined && <span className="op-tabs__count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ma'lumot ro'yxati (nom — qiymat)
// ---------------------------------------------------------------------------
export function DataList({ items }: { items: readonly { label: ReactNode; value: ReactNode }[] }) {
  return (
    <dl className="op-dl">
      {items.map((it, i) => (
        <div key={i} className="op-dl__row">
          <dt>{it.label}</dt>
          <dd>{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}
